import {AiAnswerCompletionInfo, AiStreamResponse} from 'pages/search';
import {LinearProgress, withStyles} from "@material-ui/core";
import {useEffect, useLayoutEffect, useRef, useState} from "react";
import PortalFontIcon from "./PortalFontIcon";

export type DocsAiAnswerProps = {
    
    aiStreamResponse: AiStreamResponse
    completionInfo: AiAnswerCompletionInfo
    classes?: any
}

export const styles = {
    "@global": {
        ".docsAiAnswerContainer": {
            "marginTop": "-70px",
        },
        ".label": {
            "color": "#20A2CA",
            "fontWeight": "bold",
            "fontSize": "1.6em"
            
        },
        ".module-border-wrap": {
            "maxWidth": "850px",
            "padding": "3px",
            "position": "relative",
            "background": "linear-gradient(to right, #1FCC8A, #2A19CE)",
            "fallbacks": [
                {
                    "padding": "1rem"
                }
            ],
            "margin-bottom": "30px"
        },
        ".module": {
            "background": "white",
            "color": "#292929",
            "padding": "1rem",
            "display": "flex"
        },
        ".answer": {
            "width": "75%",
            "height": "100%",
            "paddingRight": "2rem"
        },
        ".more-button": {
            "paddingTop": "1rem",
            "textAlign": "right"
        },
        ".score-and-reference": {
            "width": "25%"
        },
        ".confidence, .sources-label": {
            "fontWeight": "bold"
        },
        ".sources": {
            "paddingTop": ".25rem"
        },
        "a.source": {
            "display": "block",
            "color": "#7EA4E1"
        },
        ".sources-list": {
            "paddingTop": "0"
        },
        "@media (max-width: 850px)": {
            ".module": {
                "flexDirection": "column"
            },
            ".answer": {
                "width": "100%",
                "paddingRight": "0rem"
            },
            ".score-and-reference": {
                "width": "100%",
                "paddingTop": "1rem"
            }
        },
        ".ribbon": {
            "width": "50px",
            "height": "50px",
            "overflow": "hidden",
            "position": "absolute"
        },
        ".ribbon::before, .ribbon::after": {
            "position": "absolute",
            "zIndex": "-1",
            "content": "''",
            "display": "block",
            "border": "5px solid #2980b9"
        },
        ".ribbon span": {
            "position": "absolute",
            "display": "block",
            "width": "90px",
            "padding": "3px 0",
            "backgroundColor": "#3498db",
            "boxShadow": "0 5px 5px rgba(0,0,0,.1)",
            "color": "#fff",
            "font": "700 8px/1 'Lato', sans-serif",
            "textShadow": "0 1px 1px rgba(0,0,0,.2)",
            "textTransform": "uppercase",
            "textAlign": "center"
        },
        ".ribbon-top-right": {
            "top": "-5px",
            "right": "-5px"
        },
        ".ribbon-top-right::before, .ribbon-top-right::after": {
            "borderTopColor": "transparent",
            "borderRightColor": "transparent"
        },
        ".ribbon-top-right::before": {
            "top": "0",
            "left": "0"
        },
        ".ribbon-top-right::after": {
            "bottom": "0",
            "right": "0"
        },
        ".ribbon-top-right span": {
            "left": "-14px",
            "top": "14px",
            "transform": "rotate(45deg)"
        },
        ".answer-text-wrapper": {
            "min-height": "75px",
            "white-space": "pre-wrap",
        },
        ".answer-text-wrapper-collapsed": {
            "max-height": "75px",
            "display": "-webkit-box",
            "-webkit-line-clamp": "3",
            "-webkit-box-orient": "vertical",
            "overflow": "hidden"
        },
        ".expand-collapse-btn": {
            "display": "inline-flex",
            "cursor": "pointer",
            "float": "right"
        },
        
        ".expand-collapse-btn .material-icons": {
            "transition": "none"
        },
        ".expandCollapseHidden": {
            "visibility": "hidden"
        }
        
    }
}


const DocsAiAnswer = (props: DocsAiAnswerProps) => {
    
    const [expanded, setExpanded] = useState(false);
    const answerContainer = useRef(null)
    const [answerIsOverflowing, setAnswerIsOverflowing] = useState(false)
    
    const calculateAnswerOverflowing = () => {
        const isOverflowing = answerContainer.current.offsetHeight < answerContainer.current.scrollHeight
        setAnswerIsOverflowing(isOverflowing)
    }
    
    useLayoutEffect(() => {
        if (answerContainer.current) {
            calculateAnswerOverflowing()
        }
    })

    useEffect(() => {
        const handler = () => calculateAnswerOverflowing()
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, []);

    const {aiStreamResponse, completionInfo} = props
    
    const answerText = aiStreamResponse ? aiStreamResponse.message : undefined

    
    const done = aiStreamResponse?.done


    const sourceLinks = completionInfo?.sourceLinks?.filter(sourceLink => sourceLink && sourceLink.href && sourceLink.title)
    .map(pageLink => <a className="source" href={pageLink.href}>{pageLink.title}</a>)
    
    
    const textWrapperClass = expanded? "answer-text-wrapper answer-text-wrapper-expanded" : "answer-text-wrapper answer-text-wrapper-collapsed"

    let expandCollapseClass = 'expand-collapse-btn '
    if (!expanded && !answerIsOverflowing) {
        expandCollapseClass += 'expandCollapseHidden'
    }
        
    
    return (

        <div className="docsAiAnswerContainer">
            <span className="label">HelpAI</span>
            <div className="module-border-wrap">
                <div className="module">
                    
                    <div className="answer">
                        <div className={textWrapperClass} ref={answerContainer}>
                            {
                                !!answerText ? <div>{answerText}</div> : <LinearProgress/>
                            }
                        </div>
                        {
                            expanded ?
                                <div className={expandCollapseClass}
                                     onClick={() => setExpanded(!expanded)}><PortalFontIcon name={"expand_less"}/>Collapse</div> :
                                <div className={expandCollapseClass}
                                     onClick={() => setExpanded(!expanded)}><PortalFontIcon name={"expand_more"}/>More</div>
                        }

                    </div>
                    {
                        done && completionInfo && (
                            <div className="score-and-reference">
                                <div className="sources">
                                    <span className="sources-label">Sources:</span>
                                    {sourceLinks}
                                </div>
                            </div>)
                    }
                </div>
                <div className="ribbon ribbon-top-right"><span>Beta</span></div>
            </div>
        </div>
        
    )
}

export default withStyles(styles)(DocsAiAnswer)